/**
 * usePlayback — React hook for the DOOH player playback engine.
 *
 * Manages:
 * - Reading cached manifest from IndexedDB on boot
 * - Evaluating schedule every 60 seconds against local timezone
 * - Playing assets sequentially (image duration / video native duration)
 * - Seamless video loop via dual-element swap
 * - Fallback playlist when no slot matches
 * - Emergency override: immediate pause + display of override asset
 * - Emergency expiry or CLEAR command → resume normal schedule
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ScheduleManifest, ScheduleSlot } from '@dooh/shared';
import { loadCachedManifest } from '../cache';
import {
  evaluateSchedule,
  getPlaylistItems,
  type PlaybackItem,
} from './schedule-evaluator';
import {
  buildPlayableSignal,
  hasPlayableContent,
  publishPlayable,
} from './playable-signal';

/** Log prefix */
const LOG_PREFIX = '[Playback]';

function log(...args: unknown[]) {
  console.log(LOG_PREFIX, ...args);
}

/** Evaluation interval: every 60 seconds */
const EVALUATION_INTERVAL_MS = 60_000;

/** Emergency override state */
export interface EmergencyState {
  active: boolean;
  assetId: string | null;
  duration: number | null;
  expiresAt: string | null;
  startedAt: number | null;
}

/** Current playback state */
export interface PlaybackState {
  /** Current mode */
  mode: 'scheduled' | 'fallback' | 'emergency' | 'idle';
  /** Current playback items */
  items: PlaybackItem[];
  /** Index of the currently playing item */
  currentIndex: number;
  /** The active slot (if scheduled mode) */
  slot: ScheduleSlot | null;
  /** Emergency state */
  emergency: EmergencyState;
  /** Fallback playlist ID */
  fallbackPlaylistId: string | null;
  /** Manifest version */
  manifestVersion: string | null;
}

/** Asset metadata needed to resolve playback items */
export interface AssetMeta {
  type: 'image' | 'video';
  duration: number | null; // seconds, null for images (use default)
}

/** Options for usePlayback */
export interface UsePlaybackOptions {
  /** Screen ID for loading manifest from IndexedDB */
  screenId: string;
  /** Asset metadata map for resolving items */
  assets: Map<string, AssetMeta>;
  /** Default image display duration in seconds */
  defaultImageDuration?: number;
  /** Called when the schedule is re-evaluated */
  onEvaluation?: (state: PlaybackState) => void;
  /** Called when an asset starts playing (for proof-of-play) */
  onAssetStart?: (assetId: string) => void;
  /** Called when an asset stops playing (for proof-of-play) */
  onAssetEnd?: (assetId: string) => void;
  /** External manifest override (e.g., from MQTT message) */
  manifestOverride?: ScheduleManifest | null;
  /** Emergency override command. assetType comes from the command payload (#71)
   *  so uncached assets resolve correctly; falls back to the assets map. */
  emergencyCommand?: { type: 'EMERGENCY'; assetId: string; duration: number; expiresAt: string; assetType?: 'image' | 'video' } | { type: 'CLEAR' } | null;
  /**
   * When true, hold item/evaluation advancement (e.g. while the recycle-kiosk
   * overlay is covering the screen). No timers fire while suspended, so an
   * asset never "advances past its duration" behind the overlay. On
   * transitioning back to false, the schedule is re-evaluated to resync to
   * the wall clock (mirrors how a fresh mount would evaluate).
   */
  suspended?: boolean;
}

/** Hook return value */
export interface UsePlaybackReturn {
  /** Current playback state */
  state: PlaybackState;
  /** Current item being displayed */
  currentItem: PlaybackItem | null;
  /** Trigger manual re-evaluation */
  reevaluate: () => void;
  /** Update the manifest */
  setManifest: (manifest: ScheduleManifest | null) => void;
  /** Trigger emergency override */
  triggerEmergency: (assetId: string, duration: number, expiresAt: string, assetType?: 'image' | 'video') => void;
  /** Clear emergency override */
  clearEmergency: () => void;
  /** Advance to the next item */
  advanceToNext: () => void;
}

function createInitialState(): PlaybackState {
  return {
    mode: 'idle',
    items: [],
    currentIndex: 0,
    slot: null,
    emergency: {
      active: false,
      assetId: null,
      duration: null,
      expiresAt: null,
      startedAt: null,
    },
    fallbackPlaylistId: null,
    manifestVersion: null,
  };
}

export function usePlayback(options: UsePlaybackOptions): UsePlaybackReturn {
  const {
    screenId,
    assets,
    defaultImageDuration = 10,
    onEvaluation,
    onAssetStart,
    onAssetEnd,
    manifestOverride,
    emergencyCommand,
    suspended = false,
  } = options;

  const [state, setState] = useState<PlaybackState>(createInitialState);
  const manifestRef = useRef<ScheduleManifest | null>(null);
  const evalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const itemTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emergencyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Synced every render (like stateRef above) so timer callbacks always see
  // the latest value without needing to be re-created.
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;

  // Track previous item for onAssetEnd callback
  const prevItemRef = useRef<PlaybackItem | null>(null);
  const onAssetStartRef = useRef(onAssetStart);
  onAssetStartRef.current = onAssetStart;
  const onAssetEndRef = useRef(onAssetEnd);
  onAssetEndRef.current = onAssetEnd;
  const onEvaluationRef = useRef(onEvaluation);
  onEvaluationRef.current = onEvaluation;

  // ─── Schedule Item Advancement ──────────────────────────────────

  const scheduleNextItem = useCallback((items: PlaybackItem[], currentIndex: number) => {
    if (items.length === 0) return;

    // Clear any existing item timer
    if (itemTimerRef.current) {
      clearTimeout(itemTimerRef.current);
      itemTimerRef.current = null;
    }

    const item = items[currentIndex];
    if (!item) return;

    // Fire onAssetEnd for previous item
    if (prevItemRef.current && prevItemRef.current.assetId !== item.assetId) {
      onAssetEndRef.current?.(prevItemRef.current.assetId);
    }
    // Fire onAssetStart for current item
    onAssetStartRef.current?.(item.assetId);
    prevItemRef.current = item;

    // Schedule the advance after the item's duration — for ALL item types.
    // Videos carry their native duration from the manifest assets payload;
    // scheduled-mode videos render with loop, so their 'ended' event never
    // fires and this timer is the only advancement mechanism. (Previously
    // only images were timed and pure-video slots relied on a stale
    // image-typed timer chain from boot, cutting every video at the default
    // image duration.)
    itemTimerRef.current = setTimeout(() => {
      // Held: the overlay covered the screen for this item's remaining
      // duration. Do not advance — unsuspending triggers a fresh
      // evaluate()/reevaluate() that resyncs to the wall clock instead.
      // Clear the ref: this timer has now fired (and won't fire again),
      // so evaluate()'s "itemTimerRef.current === null" re-arm check must
      // see null here, not the id of a spent timeout — otherwise unsuspend
      // never re-arms and the screen freezes until the next slot boundary.
      if (suspendedRef.current) {
        itemTimerRef.current = null;
        return;
      }
      // Superseded: something else (advanceToNext via a non-looping video's
      // 'ended', or a slot change) already moved playback on and armed its
      // own timer chain. Bow out instead of double-advancing.
      if (
        stateRef.current.items.length > 0 &&
        stateRef.current.currentIndex !== currentIndex
      ) {
        itemTimerRef.current = null;
        return;
      }
      setState((prev) => {
        const nextIndex = (prev.currentIndex + 1) % prev.items.length;
        return { ...prev, currentIndex: nextIndex };
      });
      // Chain from the LIVE items so late-arriving asset metadata (types /
      // native durations refreshed after pre-download) applies from the next
      // item onward — never an immortal chain of the array captured at boot.
      const liveItems = stateRef.current.items.length > 0 ? stateRef.current.items : items;
      scheduleNextItem(liveItems, (currentIndex + 1) % liveItems.length);
    }, item.duration * 1000);
  }, []);

  // Re-arms the item timer for the item already playing, WITHOUT re-firing
  // onAssetStart/onAssetEnd (unlike scheduleNextItem, which always fires
  // onAssetStart for the item it's given — appropriate for a genuinely new
  // item, but a duplicate/incorrect proof-of-play signal for an item that
  // never stopped playing, e.g. resuming after the recycle-kiosk overlay).
  const armItemTimer = useCallback((items: PlaybackItem[], currentIndex: number) => {
    if (items.length === 0) return;

    // Guard against double-arming: always clear any existing timer first.
    if (itemTimerRef.current) {
      clearTimeout(itemTimerRef.current);
      itemTimerRef.current = null;
    }

    const item = items[currentIndex];
    if (!item) return;

    itemTimerRef.current = setTimeout(() => {
      // Same rationale as in scheduleNextItem: clear the ref on the
      // suspended early-return so a later re-arm check sees null, not a
      // spent timeout id.
      if (suspendedRef.current) {
        itemTimerRef.current = null;
        return;
      }
      if (
        stateRef.current.items.length > 0 &&
        stateRef.current.currentIndex !== currentIndex
      ) {
        itemTimerRef.current = null;
        return;
      }
      setState((prev) => {
        const nextIndex = (prev.currentIndex + 1) % prev.items.length;
        return { ...prev, currentIndex: nextIndex };
      });
      const liveItems = stateRef.current.items.length > 0 ? stateRef.current.items : items;
      scheduleNextItem(liveItems, (currentIndex + 1) % liveItems.length);
    }, item.duration * 1000);
  }, [scheduleNextItem]);

  // ─── Evaluation ──────────────────────────────────────────────────

  const evaluate = useCallback((opts?: { ignoreEmergency?: boolean }) => {
    // Don't evaluate during emergency — EXCEPT on the clear/expiry path, which
    // passes ignoreEmergency because it flips emergency.active off in a
    // setState that hasn't committed yet (#190). Without the override, the
    // synchronous re-eval reads the stale active=true from stateRef and
    // early-returns, so recovery from a cleared emergency waited for the next
    // 60s tick — up to a minute of "Initializing…" dead air.
    if (!opts?.ignoreEmergency && stateRef.current.emergency.active) {
      log('Skipping evaluation: emergency active');
      return;
    }

    const manifest = manifestRef.current;
    const now = new Date();

    log('Evaluating schedule at', now.toISOString());
    const evaluation = evaluateSchedule(manifest, now);

    if (evaluation.mode === 'scheduled' && evaluation.slot) {
      const items = getPlaylistItems(evaluation.slot, assets, defaultImageDuration);
      log(`Scheduled: slot ${evaluation.slot.id}, ${items.length} items`);

      // Preserve currentIndex when the slot hasn't changed (#50) — previously
      // every 60s re-evaluation reset to 0, causing the first item in a
      // multi-asset slot to be over-represented.
      const currentSlot = stateRef.current.slot;
      const sameSlot = currentSlot && currentSlot.id === evaluation.slot.id;
      const preserveIndex = sameSlot && stateRef.current.currentIndex < items.length
        ? stateRef.current.currentIndex
        : 0;

      const newState: PlaybackState = {
        mode: 'scheduled',
        items,
        currentIndex: preserveIndex,
        slot: evaluation.slot,
        emergency: { active: false, assetId: null, duration: null, expiresAt: null, startedAt: null },
        fallbackPlaylistId: evaluation.fallbackPlaylistId,
        manifestVersion: manifest?.version ?? null,
      };

      setState(newState);
      onEvaluationRef.current?.(newState);
      publishPlayable(
        buildPlayableSignal({
          hasContent: hasPlayableContent(newState),
          manifestValidUntil: manifest?.validUntil ?? null,
          now,
        }),
      );

      // Only (re)start item advancement with fresh onAssetStart semantics if
      // the slot changed (new playback session). If the slot is the same,
      // the existing timer normally continues advancing on its own — EXCEPT
      // when something cleared it out from under us without rescheduling
      // (e.g. the recycle-kiosk overlay's suspend path drops the timer, and
      // a timer callback that fires while suspended returns early instead of
      // re-arming). Detect that case via itemTimerRef being null while items
      // exist, and re-arm without re-firing start/end for the item that
      // never actually stopped playing.
      if (!sameSlot && items.length > 0) {
        scheduleNextItem(items, preserveIndex);
      } else if (sameSlot && items.length > 0 && itemTimerRef.current === null) {
        armItemTimer(items, preserveIndex);
      }
    } else {
      log('No matching slot, entering fallback mode');
      const newState: PlaybackState = {
        mode: 'fallback',
        items: [],
        currentIndex: 0,
        slot: null,
        emergency: { active: false, assetId: null, duration: null, expiresAt: null, startedAt: null },
        fallbackPlaylistId: evaluation.fallbackPlaylistId,
        manifestVersion: manifest?.version ?? null,
      };

      setState(newState);
      onEvaluationRef.current?.(newState);
      publishPlayable(
        buildPlayableSignal({
          hasContent: hasPlayableContent(newState),
          manifestValidUntil: manifest?.validUntil ?? null,
          now,
        }),
      );

      // Clear item timer since no items to play
      if (itemTimerRef.current) {
        clearTimeout(itemTimerRef.current);
        itemTimerRef.current = null;
      }
    }
  }, [assets, defaultImageDuration, scheduleNextItem, armItemTimer]);

  // ─── Load manifest on boot ────────────────────────────────────────

  useEffect(() => {
    async function loadManifest() {
      try {
        const result = await loadCachedManifest(screenId);
        if (result) {
          log(`Loaded cached manifest v${result.manifest.version}`);
          manifestRef.current = result.manifest;
        } else {
          log('No cached manifest found');
        }
      } catch (err) {
        console.error(LOG_PREFIX, 'Failed to load cached manifest:', err);
      }
      // Initial evaluation after loading manifest
      evaluate();
    }

    loadManifest();
  }, [screenId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Manifest override (from MQTT) ────────────────────────────────

  useEffect(() => {
    if (manifestOverride !== undefined) {
      manifestRef.current = manifestOverride;
      log('Manifest updated externally');
      evaluate();
    }
  }, [manifestOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Evaluation timer (every 60s) ─────────────────────────────────

  useEffect(() => {
    evalTimerRef.current = setInterval(() => {
      // Held while suspended (e.g. recycle-kiosk overlay covering the
      // screen) — unsuspend triggers its own resync evaluate() instead.
      if (suspendedRef.current) {
        log('Skipping periodic evaluation: suspended');
        return;
      }
      evaluate();
    }, EVALUATION_INTERVAL_MS);

    return () => {
      if (evalTimerRef.current) {
        clearInterval(evalTimerRef.current);
        evalTimerRef.current = null;
      }
    };
  }, [evaluate]);

  // ─── Emergency override ───────────────────────────────────────────

  // clearEmergencyInternal must be declared before triggerEmergency (#28)
  // because triggerEmergency references it for the early-expiry guard.
  const clearEmergencyInternal = useCallback(() => {
    if (emergencyTimerRef.current) {
      clearTimeout(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      mode: 'idle',
      emergency: { active: false, assetId: null, duration: null, expiresAt: null, startedAt: null },
    }));

    // Re-evaluate immediately. stateRef still reports emergency.active=true
    // here (the setState above hasn't committed), so pass ignoreEmergency to
    // bypass the guard and resync now instead of waiting for the 60s tick
    // (#190).
    evaluate({ ignoreEmergency: true });
  }, [evaluate]);

  const triggerEmergency = useCallback((assetId: string, duration: number, expiresAt: string, assetTypeHint?: 'image' | 'video') => {
    log(`EMERGENCY override: asset=${assetId}, duration=${duration}s, expiresAt=${expiresAt}`);

    // ─── #28: Clear immediately if already expired on arrival ───────────
    // A delayed/retried command may arrive with expiresAt already in the
    // past (clock skew, MQTT delay, replay). Without this guard, no timer
    // is armed and the device is stuck in emergency mode forever.
    const expiresAtMs = new Date(expiresAt).getTime();
    const msUntilExpiry = expiresAtMs - Date.now();
    if (msUntilExpiry <= 0) {
      log('Emergency command arrived already expired — ignoring');
      clearEmergencyInternal();
      return;
    }

    // Clear any existing timers
    if (itemTimerRef.current) {
      clearTimeout(itemTimerRef.current);
      itemTimerRef.current = null;
    }
    if (emergencyTimerRef.current) {
      clearTimeout(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }

    const startedAt = Date.now();
    const emergencyState: EmergencyState = {
      active: true,
      assetId,
      duration,
      expiresAt,
      startedAt,
    };

    // Resolve asset type: prefer the type delivered with the command (#71) —
    // the metadata map is empty for creative the device hasn't cached yet —
    // then fall back to the metadata map (#27), then 'image'.
    const assetMeta = assets.get(assetId);
    const assetType = assetTypeHint ?? assetMeta?.type ?? 'image';

    // Create a single emergency item
    const emergencyItem: PlaybackItem = {
      assetId,
      assetType,
      duration,
    };

    const newState: PlaybackState = {
      mode: 'emergency',
      items: [emergencyItem],
      currentIndex: 0,
      slot: null,
      emergency: emergencyState,
      fallbackPlaylistId: stateRef.current.fallbackPlaylistId,
      manifestVersion: stateRef.current.manifestVersion,
    };

    setState(newState);
    onEvaluationRef.current?.(newState);
    publishPlayable(
      buildPlayableSignal({
        hasContent: hasPlayableContent(newState),
        manifestValidUntil: manifestRef.current?.validUntil ?? null,
      }),
    );
    onAssetStartRef.current?.(assetId);

    // Set expiry timer (guaranteed > 0 due to the early-return above)
    emergencyTimerRef.current = setTimeout(() => {
      log('Emergency expired, resuming normal schedule');
      clearEmergencyInternal();
    }, msUntilExpiry);
  }, [assets, clearEmergencyInternal]);

  const clearEmergency = useCallback(() => {
    log('Emergency CLEAR command received, resuming schedule');
    clearEmergencyInternal();
  }, [clearEmergencyInternal]);

  // ─── Handle emergency commands from props ─────────────────────────

  useEffect(() => {
    if (!emergencyCommand) return;

    if (emergencyCommand.type === 'EMERGENCY') {
      triggerEmergency(
        emergencyCommand.assetId,
        emergencyCommand.duration,
        emergencyCommand.expiresAt,
        emergencyCommand.assetType,
      );
    } else if (emergencyCommand.type === 'CLEAR') {
      clearEmergency();
    }
  }, [emergencyCommand]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Manual re-evaluation ─────────────────────────────────────────

  const reevaluate = useCallback(() => {
    log('Manual re-evaluation triggered');
    evaluate();
  }, [evaluate]);

  // ─── Suspend / unsuspend (recycle-kiosk overlay) ──────────────────

  const prevSuspendedRef = useRef(suspended);

  useEffect(() => {
    if (prevSuspendedRef.current === suspended) return;
    prevSuspendedRef.current = suspended;

    if (suspended) {
      log('Suspended: holding item advancement');
      // Held: also drop the pending item timer outright so a stale timeout
      // can't advance state the instant it fires while covered.
      if (itemTimerRef.current) {
        clearTimeout(itemTimerRef.current);
        itemTimerRef.current = null;
      }
    } else {
      log('Unsuspended: resyncing to wall clock');
      reevaluate();
    }
  }, [suspended, reevaluate]);

  // ─── Set manifest externally ──────────────────────────────────────

  const setManifest = useCallback((manifest: ScheduleManifest | null) => {
    manifestRef.current = manifest;
    evaluate();
  }, [evaluate]);

  // ─── Advance to next item ─────────────────────────────────────────

  const advanceToNext = useCallback(() => {
    setState((prev) => {
      if (prev.items.length === 0) return prev;
      const nextIndex = (prev.currentIndex + 1) % prev.items.length;

      // Clear old item timer
      if (itemTimerRef.current) {
        clearTimeout(itemTimerRef.current);
        itemTimerRef.current = null;
      }

      const item = prev.items[nextIndex];
      if (item) {
        // #180: a single-item slot self-advances (nextIndex === currentIndex).
        // When that self-advance is driven by a VideoPlayer error retry, the
        // item never genuinely (re)started — firing onAssetStart every error
        // cycle spams false proof-of-play. Only fire start/end when we actually
        // moved to a DIFFERENT item; the item timer chain still re-fires
        // onAssetStart at the item's real duration cadence for healthy loops.
        const movedToDifferentItem = nextIndex !== prev.currentIndex;
        if (movedToDifferentItem) {
          if (prevItemRef.current && prevItemRef.current.assetId !== item.assetId) {
            onAssetEndRef.current?.(prevItemRef.current.assetId);
          }
          onAssetStartRef.current?.(item.assetId);
          prevItemRef.current = item;
        }

        // Schedule the next advance for ALL item types (see scheduleNextItem)
        itemTimerRef.current = setTimeout(() => {
          // Same rationale as in scheduleNextItem: clear the ref on the
          // suspended early-return so a later re-arm check sees null, not
          // a spent timeout id.
          if (suspendedRef.current) {
            itemTimerRef.current = null;
            return;
          }
          if (
            stateRef.current.items.length > 0 &&
            stateRef.current.currentIndex !== nextIndex
          ) {
            itemTimerRef.current = null;
            return;
          }
          setState((p) => {
            const ni = (p.currentIndex + 1) % p.items.length;
            return { ...p, currentIndex: ni };
          });
          const liveItems = stateRef.current.items.length > 0 ? stateRef.current.items : prev.items;
          scheduleNextItem(liveItems, (nextIndex + 1) % liveItems.length);
        }, item.duration * 1000);
      }

      return { ...prev, currentIndex: nextIndex };
    });
  }, [scheduleNextItem]);

  // ─── Cleanup ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (evalTimerRef.current) clearInterval(evalTimerRef.current);
      if (itemTimerRef.current) clearTimeout(itemTimerRef.current);
      if (emergencyTimerRef.current) clearTimeout(emergencyTimerRef.current);
    };
  }, []);

  // ─── Derived state ────────────────────────────────────────────────

  const currentItem = state.items[state.currentIndex] ?? null;

  return {
    state,
    currentItem,
    reevaluate,
    setManifest,
    triggerEmergency,
    clearEmergency,
    advanceToNext,
  };
}
